import { differenceInMinutes, parseISO } from 'date-fns';
import { type OrderDetailV2Get as OrderDetail } from '@hungryroot/rest';

export const ONBOARDING_PROGRESS_LOCAL_STORAGE_KEY = 'onboarding-progress';

export const PENDING_CART_OPEN_AFTER_FIRST_ORDER_TOUR_KEY =
  'pending-cart-open-after-first-order-tour';

export enum OnboardingProgress {
  ShowCustomizeModal = 'show-customize-modal',
  ShowFirstPopover = 'show-first-popover',
  ShowSecondPopover = 'show-second-popover',
}

const setOnboardingProgress = (nextStep: OnboardingProgress) =>
  localStorage.setItem(ONBOARDING_PROGRESS_LOCAL_STORAGE_KEY, nextStep);

const getOnboardingProgress = () =>
  localStorage.getItem(ONBOARDING_PROGRESS_LOCAL_STORAGE_KEY);

const deleteOnboardingProgress = () =>
  localStorage.removeItem(ONBOARDING_PROGRESS_LOCAL_STORAGE_KEY);

function safeSessionGetItem(key: string): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }

  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSetItem(key: string, value: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Private mode / quota
  }
}

function safeSessionRemoveItem(key: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Private mode / quota
  }
}

export function setPendingCartOpenAfterFirstOrderTour(): void {
  safeSessionSetItem(PENDING_CART_OPEN_AFTER_FIRST_ORDER_TOUR_KEY, '1');
}

export function hasPendingCartOpenAfterFirstOrderTour(): boolean {
  return (
    safeSessionGetItem(PENDING_CART_OPEN_AFTER_FIRST_ORDER_TOUR_KEY) === '1'
  );
}

export function consumePendingCartOpenAfterFirstOrderTour(): boolean {
  if (!hasPendingCartOpenAfterFirstOrderTour()) {
    return false;
  }

  safeSessionRemoveItem(PENDING_CART_OPEN_AFTER_FIRST_ORDER_TOUR_KEY);
  return true;
}

// This has a near-identical implementation to the onboarding package.
export function useOnboardingProgress(
  order: Pick<OrderDetail, 'cutoff_datetime' | 'sequence'> | undefined
) {
  if (order === undefined) {
    return undefined;
  }

  const cutoffDate = parseISO(order.cutoff_datetime);
  const now = new Date();
  const minutesUntilCutoff = differenceInMinutes(cutoffDate, now);

  if (minutesUntilCutoff < 0) {
    deleteOnboardingProgress();
    return undefined;
  }

  return {
    setOnboardingProgress,
    deleteOnboardingProgress,
    getOnboardingProgress,
  };
}
